/**
 * Lightbox Component
 *
 * Full-screen image viewer with original/edited toggle, attempt navigation,
 * download functionality, and retry button.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Tailwind CSS core utilities only
 * - Toggle: "AI Edited" ↔ "Original"
 * - Previous/Next arrows for attempt navigation
 * - ESC key + click-outside to close
 * - Dark overlay (professional appearance)
 * - Mobile Gestures: Pinch-to-zoom, Pan
 */

import React, { useState, useEffect, useRef } from 'react';
import { useGesture } from '@use-gesture/react';
import { motion } from 'framer-motion';
import { OrderDocument, OrderStatus } from '@/shared/types';
import { Button } from '@/components/ui';
import { RetryButton } from './RetryButton';
import { getServiceName, getServiceIcon, formatDate } from '@/utils/formatting';
import { downloadOrderResult } from '@/utils/download';

export interface LightboxProps {
  order: OrderDocument;
  isOpen: boolean;
  onClose: () => void;
  onRetry?: (orderId: string) => Promise<void>;
  descriptionText?: string | null;
}

/**
 * Lightbox Component
 *
 * @param order - Order to display
 * @param isOpen - Whether lightbox is open
 * @param onClose - Close callback
 * @param onRetry - Optional retry callback
 */
export function Lightbox({
  order,
  isOpen,
  onClose,
  onRetry,
  descriptionText,
}: LightboxProps): React.JSX.Element | null {
  const [showOriginal, setShowOriginal] = useState<boolean>(false);
  const [currentAttemptIndex, setCurrentAttemptIndex] = useState<number>(0);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  // Gesture state
  const [style, setStyle] = useState({ x: 0, y: 0, scale: 1, rotate: 0 });
  const imageRef = useRef<HTMLDivElement>(null);

  // Reset state when order changes
  useEffect(() => {
    setShowOriginal(false);
    setCurrentAttemptIndex(order.attempts.length - 1); // Start with latest attempt
    setStyle({ x: 0, y: 0, scale: 1, rotate: 0 }); // Reset gestures
  }, [order.orderId, order.attempts.length]);

  // Reset gestures when switching images
  useEffect(() => {
    setStyle({ x: 0, y: 0, scale: 1, rotate: 0 });
  }, [showOriginal, currentAttemptIndex]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle click outside
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Gesture handler
  useGesture({
    onDrag: ({ offset: [x, y] }) => {
      setStyle(prev => ({ ...prev, x, y }));
    },
    onPinch: ({ offset: [d] }) => {
      // Map pinch distance to scale (1 + distance / factor)
      // d starts at 0
      const newScale = 1 + d / 200;
      setStyle(prev => ({ ...prev, scale: Math.max(0.5, Math.min(4, newScale)) }));
    },
  }, {
    target: imageRef,
    drag: {
      from: () => [style.x, style.y],
    },
    pinch: {
      scaleBounds: { min: 0.5, max: 4 },
      modifierKey: null, // Enable pinch without modifier key on trackpads/touch
    }
  });

  // Image Control Handlers
  const handleRotate = (direction: 'left' | 'right') => {
    setStyle(prev => ({
      ...prev,
      rotate: prev.rotate + (direction === 'left' ? -90 : 90)
    }));
  };

  const handleZoom = (direction: 'in' | 'out') => {
    setStyle(prev => {
      const newScale = direction === 'in' ? prev.scale + 0.5 : prev.scale - 0.5;
      return {
        ...prev,
        scale: Math.max(0.5, Math.min(4, newScale))
      };
    });
  };

  const handleReset = () => {
    setStyle({ x: 0, y: 0, scale: 1, rotate: 0 });
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const serviceName = getServiceName(order.serviceType);
  const serviceIcon = getServiceIcon(order.serviceType);
  const currentAttempt = order.attempts[currentAttemptIndex];
  const totalAttempts = order.attempts.length;

  // Determine which image to show
  const displayImage = showOriginal ? order.inputImageUrl : currentAttempt?.resultUrl;

  // Handle download
  const handleDownload = async (): Promise<void> => {
    if (!displayImage) return;

    setDownloading(true);
    try {
      if (showOriginal) {
        await downloadOrderResult(
          displayImage,
          order.serviceType,
          0, // Original is attempt 0
          'original.jpg'
        );
      } else {
        await downloadOrderResult(
          displayImage,
          order.serviceType,
          currentAttempt.attemptNumber
        );
      }
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(false);
    }
  };

  // Navigate attempts
  const goToPreviousAttempt = (): void => {
    if (currentAttemptIndex > 0) {
      setCurrentAttemptIndex(currentAttemptIndex - 1);
      setShowOriginal(false);
    }
  };

  const goToNextAttempt = (): void => {
    if (currentAttemptIndex < totalAttempts - 1) {
      setCurrentAttemptIndex(currentAttemptIndex + 1);
      setShowOriginal(false);
    }
  };

  const handleCopyText = async (): Promise<void> => {
    if (descriptionText) {
      try {
        await navigator.clipboard.writeText(descriptionText);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4 touch-none"
      onClick={handleOverlayClick}
    >
      {/* Lightbox Content */}
      <div className="relative max-w-7xl w-full max-h-screen flex flex-col h-full">
        {/* Header */}
        <div className="bg-gray-900 bg-opacity-80 text-white px-6 py-4 rounded-t-lg flex items-center justify-between z-10 shrink-0">
          <div className="flex items-center space-x-3">
            <span className="text-3xl" role="img" aria-label={serviceName}>
              {serviceIcon}
            </span>
            <div>
              <h2 className="text-xl font-bold">{serviceName}</h2>
              <p className="text-sm text-gray-300">
                Created: {formatDate(order.createdAt)}
              </p>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white transition-colors duration-200 p-2"
            aria-label="Close lightbox"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Image Container */}
        <div className="relative flex-1 bg-gray-800 flex items-center justify-center overflow-hidden touch-none">
          {displayImage ? (
            <motion.div
              ref={imageRef}
              className="cursor-grab active:cursor-grabbing touch-none"
              animate={{ x: style.x, y: style.y, scale: style.scale, rotate: style.rotate }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{ touchAction: 'none' }}
            >
              <img
                src={displayImage}
                alt={showOriginal ? 'Original image' : `${serviceName} result`}
                className="max-w-full max-h-[70vh] object-contain pointer-events-none" // pointer-events-none prevents browser drag
              />
            </motion.div>
          ) : (
            <div className="text-white text-center">
              <p className="text-xl mb-2">No image available</p>
              <p className="text-sm text-gray-400">
                {currentAttempt?.status === OrderStatus.PROCESSING
                  ? 'Processing...'
                  : currentAttempt?.status === OrderStatus.FAILED
                    ? 'Processing failed'
                    : 'Image not found'}
              </p>
            </div>
          )}

          {/* Attempt Navigation Arrows (when multiple attempts) */}
          {totalAttempts > 1 && !showOriginal && (
            <>
              {currentAttemptIndex > 0 && (
                <button
                  onClick={goToPreviousAttempt}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-200 z-10"
                  aria-label="Previous attempt"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              {currentAttemptIndex < totalAttempts - 1 && (
                <button
                  onClick={goToNextAttempt}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-200 z-10"
                  aria-label="Next attempt"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </>
          )}

          {/* Attempt Indicator */}
          {!showOriginal && currentAttempt && (
            <div
              className="absolute top-4 left-4 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-opacity-90 active:scale-95 transition-all select-none z-10"
              onClick={() => {
                if (totalAttempts > 1) {
                  setCurrentAttemptIndex((prev) => (prev + 1) % totalAttempts);
                }
              }}
              title="Click to cycle attempts"
            >
              <p className="text-sm font-semibold">
                Attempt {currentAttempt.attemptNumber} of {order.maxAttempts}
              </p>
            </div>
          )}



        </div>

        {/* Footer / Controls */}
        <div className="bg-gray-900 bg-opacity-80 text-white px-6 py-4 rounded-b-lg flex flex-col gap-4 shrink-0 z-10">
          {/* Row 1: Manipulation Controls */}
          {displayImage && (
            <div className="flex flex-wrap items-center justify-center gap-4">
              {/* Toggle Button */}
              {order.inputImageUrl && (
                <button
                  onClick={() => setShowOriginal(!showOriginal)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-sm transition-all duration-200 hover:scale-105"
                >
                  {showOriginal ? 'View AI Edited' : 'View Original'}
                </button>
              )}

              <div className="w-px h-8 bg-gray-700 mx-2 hidden sm:block"></div>

              {/* Rotate Left */}
              <button
                onClick={() => handleRotate('left')}
                className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
                title="Rotate Left"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </button>

              {/* Rotate Right */}
              <button
                onClick={() => handleRotate('right')}
                className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
                title="Rotate Right"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                </svg>
              </button>

              {/* Zoom Out */}
              <button
                onClick={() => handleZoom('out')}
                className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom Out"
                disabled={style.scale <= 0.5}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>

              {/* Zoom In */}
              <button
                onClick={() => handleZoom('in')}
                className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom In"
                disabled={style.scale >= 4}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>

              {/* Reset */}
              {(style.rotate !== 0 || style.scale !== 1) && (
                <button
                  onClick={handleReset}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-800 rounded-full transition-colors"
                  title="Reset View"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Separator */}
          <div className="border-t border-gray-700"></div>

          {/* Row 2: Action Buttons */}
          <div className="flex flex-wrap gap-3 justify-center">
            {/* Download Button */}
            {displayImage && (
              <Button
                variant="secondary"
                onClick={handleDownload}
                disabled={downloading}
                loading={downloading}
              >
                Download {showOriginal ? 'Original' : `Attempt ${currentAttempt.attemptNumber}`}
              </Button>
            )}

            {/* Retry Button */}
            {onRetry && !showOriginal && (
              <RetryButton order={order} onRetry={onRetry} />
            )}
          </div>

          {/* Status Message */}
          {currentAttempt?.status === OrderStatus.PROCESSING && (
            <div className="bg-blue-900 bg-opacity-50 border border-blue-500 text-blue-200 px-4 py-2 rounded-lg text-center">
              <p className="text-sm">Processing in progress... Check back soon!</p>
            </div>
          )}

          {currentAttempt?.status === OrderStatus.FAILED && currentAttempt?.errorMessage && (
            <div className="bg-red-900 bg-opacity-50 border border-red-500 text-red-200 px-4 py-2 rounded-lg text-center">
              <p className="text-sm">{currentAttempt.errorMessage}</p>
            </div>
          )}
        </div>

        {/* Description Text Overlay */}
        {descriptionText && (
          <div className="absolute inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto pointer-events-auto relative">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-gray-900">Description</h3>
                <Button
                  variant="secondary"
                  onClick={handleCopyText}
                  className="flex items-center space-x-2"
                  aria-label="Copy description to clipboard"
                >
                  {copySuccess ? (
                    <>
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </Button>
              </div>
              <div className="prose max-w-none">
                <p className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {descriptionText}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
